import BreadcrumbPanel from "../../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import {
  DATA_EXPLORERS,
  DATA_LIBRARY,
  resolveDataLibraryLens,
} from "../../../../../../shared/lib/routesResolver";
import { Link, useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../../../../hooks/useAxios";
import useLoading from "../../../../../../shared/components/loading/useLoading";
import { useContext, useEffect, useState } from "react";
import DataLibraryContext from "../../../../../../contexts/dataLibraryContext";
import { useTracking } from "react-tracking";
import {
  API_DATASET_DIMENSIONS,
  API_DATASET_LIBRARY_BY_ID,
  API_DATASET_METRICS,
} from "../../../../../../shared/lib/apiEndpointsResolver";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../../../shared/components/tabs/Tabs";
import DataLibraryDocsField from "./DataLibraryDocsField";
import { Button } from "../../../../../../shared/components/button/Button";

const DataLibraryDocs = () => {
  const { id } = useParams();
  const axios = useAxios();
  const { loading, setLoading } = useLoading();
  const context = useContext(DataLibraryContext);
  const [dataset, setDataset] = useState(context.dataset);
  const { trackEvent } = useTracking();
  const navigate = useNavigate();
  // ************************************
  const [dimensions, setDimensions] = useState(context.dimensions);
  const [metrics, setMetrics] = useState(context.metrics);

  const breadcrumbItems = [
    { title: "dataDocs.overview.breadcrumb.first", link: DATA_EXPLORERS },
    { title: "dataDocs.overview.breadcrumb.second", link: DATA_LIBRARY },
    {
      title: "dataDocs.overview.breadcrumb.third",
      titleOptions: { dataset: dataset?.name },
    },
  ];

  const findDataset = () => {
    setLoading(true);
    axios
      .get(API_DATASET_LIBRARY_BY_ID(id))
      .then((response) => {
        setDataset(response.data);
        context.setDataset(response.data);
      })
      .finally(() => setLoading(false));
  };

  const findDatasetDimensions = () => {
    axios.get(API_DATASET_DIMENSIONS(id)).then((response) => {
      const dimensions = response.data.map((dimension) => {
        return { ...dimension, possibleValues: [] };
      });
      setDimensions(dimensions);
      context.setDimensions(dimensions);
    });
  };

  const findDatasetMetrics = () => {
    axios.get(API_DATASET_METRICS(id)).then((response) => {
      setMetrics(response.data);
      context.setMetrics(response.data);
    });
  };

  useEffect(() => {
    if (dataset === undefined || id !== dataset.code) {
      findDataset();
      findDatasetDimensions();
      findDatasetMetrics();
    }
    trackEvent({
      page: "datasets-library-docs",
      event: "page-load",
      data: { id: id },
    });
  }, [id]);

  return (
    <>
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <div className="min-h-screen bg-gray-50 px-6 pb-6">
        <div>
          <div className="flex flex-row justify-between align-middle">
            <div>
              <h2 className="text-2xl font-bold tracking-tight">
                {dataset?.name}
              </h2>
              <p className="text-muted-foreground">{dataset?.description}</p>
            </div>
            <Button size="sm" asChild>
              <Link to={resolveDataLibraryLens(id)} state={{ id: id }}>
                Go to Data Lens
              </Link>
            </Button>
          </div>
        </div>
        <Tabs defaultValue="metrics" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="metrics">Metrics</TabsTrigger>
            <TabsTrigger value="dimensions">Dimensions</TabsTrigger>
          </TabsList>
          <TabsContent value="metrics" className="space-y-4">
            {metrics.length > 0 ? (
              <>
                {metrics.map((metric) => (
                  <DataLibraryDocsField
                    key={metric?.id}
                    code={metric?.code}
                    name={metric?.name}
                    description={metric?.description}
                  />
                ))}
              </>
            ) : (
              <div className="flex flex-row justify-center text-muted-foreground">
                No metrics available
              </div>
            )}
          </TabsContent>
          <TabsContent value="dimensions" className="space-y-4">
            {dimensions.length > 0 ? (
              <>
                {dimensions.map((dimension) => (
                  <DataLibraryDocsField
                    key={dimension?.id}
                    code={dimension?.code}
                    name={dimension?.name}
                    description={dimension?.description}
                    dataType={dimension?.dimensionType?.description}
                  />
                ))}
              </>
            ) : (
              <div className="flex flex-row justify-center text-muted-foreground">
                No dimensions available
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default DataLibraryDocs;
